import { useMemo, useState } from "react";
import "./AppointmentsPage.css";
import { useAuth } from "../../../hooks/useAuth";
import { useEntitlements } from "../../../hooks/useEntitlements";
import { useAppointments } from "../../../hooks/useAppointments";
import { useClientLookup } from "../../../hooks/useClientLookup";
import { useBranches } from "../../../hooks/useBranches";
import { useServices } from "../../../hooks/useServices";
import { useUsers } from "../../../hooks/useUsers";
import {
  createAppointmentAsync,
  deleteAppointmentAsync,
  getAppointmentsByClientAsync,
  updateAppointmentAsync,
} from "../../../api/AppointmentApi";
import { createFollowUpAsync } from "../../../api/FollowUpApi";
import {
  Appointment,
  AppointmentStatus,
  AppointmentUpdateModel,
  CreateAppointmentRequest,
} from "../../../models/AppointmentModels";
import { CreateFollowUpRequest, FollowUpStatus } from "../../../models/FollowUpModels";
import { Service } from "../../../models/ServiceModels";
import { Constants } from "../../../constants/constants";
import { OrgForms } from "../../../config/forms";
import AppointmentModal, {
  AppointmentFormValues,
  AppointmentModalMode,
} from "./AppointmentModal";
import ClientModal, { ClientFormValues } from "../clients/ClientModal";
import FollowUpModal, { FollowUpFormValues } from "../followups/FollowUpModal";
import ClientHistoryModal from "./ClientHistoryModal";
import Toast from "../../../components/Toast";
import SubscriptionSectionGate from "../../../components/SubscriptionSectionGate";
import AppointmentStatusBadge, {
  appointmentStatusLabel,
} from "../../../components/AppointmentStatusBadge";
import {
  formatDateTime,
  monthRange,
} from "../../../utils/DateTime";
import DateRangeFilter, {
  DateRangeValue,
  emptyDateRangeValue,
  resolveDateRange,
} from "../../../components/DateRangeFilter";

const toServiceInputs = (
  form: AppointmentFormValues,
  serviceMap: Map<string, Service>,
) =>
  form.services
    .filter((line) => line.serviceId)
    .map((line) => {
      const service = serviceMap.get(line.serviceId);
      return {
        serviceId: line.serviceId,
        serviceName: service?.name,
        price: service?.price ?? 0,
        durationMinutes: service?.durationMinutes ?? 30,
        quantity: Math.max(1, Number(line.quantity) || 1),
      };
    });

interface IconProps {
  title?: string;
}

const EditIcon = ({ title }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={title ? undefined : true}
  >
    {title && <title>{title}</title>}
    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
  </svg>
);

const CompleteIcon = ({ title }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={title ? undefined : true}
  >
    {title && <title>{title}</title>}
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const FollowUpIcon = ({ title }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={title ? undefined : true}
  >
    {title && <title>{title}</title>}
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const DeleteIcon = ({ title }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={title ? undefined : true}
  >
    {title && <title>{title}</title>}
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const AppointmentsPage = () => {
  const { user, hasPrivilege } = useAuth();
  const organizationId = user?.activeOrganization?.organizationId;
  const canCreateAppointments = hasPrivilege(
    OrgForms.Appointments,
    "canCreate",
  );
  const canEditAppointments = hasPrivilege(OrgForms.Appointments, "canEdit");
  const canDeleteAppointments = hasPrivilege(
    OrgForms.Appointments,
    "canDelete",
  );
  const canCreateFollowUps = hasPrivilege(OrgForms.FollowUps, "canCreate");
  const canCreateClients = hasPrivilege(OrgForms.Clients, "canCreate");
  const { entitlements, loading: entitlementsLoading } = useEntitlements();

  const {
    appointments,
    pagination,
    loading,
    error,
    filters,
    setFilters,
    setPage,
    reload,
  } = useAppointments(organizationId);

  const { branches } = useBranches(organizationId);
  const { services } = useServices(organizationId);
  const { users: staff } = useUsers(organizationId, 100);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<AppointmentModalMode>("create");
  const [selectedAppointment, setSelectedAppointment] =
    useState<Appointment | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [range, setRange] = useState<DateRangeValue>(emptyDateRangeValue());
  const [submitting, setSubmitting] = useState(false);
  const clientPicker = useClientLookup(organizationId);
  const [followUpTarget, setFollowUpTarget] = useState<Appointment | null>(
    null,
  );
  const [historyClient, setHistoryClient] = useState<Appointment | null>(null);
  const [historyAppointments, setHistoryAppointments] = useState<Appointment[]>(
    [],
  );
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info";
  } | null>(null);

  const branchMap = useMemo(
    () => new Map(branches.map((branch) => [branch.id, branch])),
    [branches],
  );
  const serviceMap = useMemo(
    () => new Map(services.map((service) => [service.id, service])),
    [services],
  );
  const staffMap = useMemo(
    () => new Map(staff.map((member) => [member.id, member])),
    [staff],
  );

  const openModal = (mode: AppointmentModalMode, appointment: Appointment | null) => {
    clientPicker.clearCreatedClient();
    setModalMode(mode);
    setSelectedAppointment(appointment);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    clientPicker.clearCreatedClient();
  };

  const handleRangeChange = (next: DateRangeValue) => {
    setRange(next);
    setFilters(resolveDateRange(next));
  };

  const showToast = (
    message: string,
    type: "success" | "error" | "info",
  ) => setToast({ message, type });

  const handleSubmit = async (form: AppointmentFormValues) => {
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    const serviceInputs = toServiceInputs(form, serviceMap);
    setSubmitting(true);
    try {
      if (modalMode === "create") {
        if (!organizationId) {
          showToast("No active organization", "error");
          return;
        }
        const payload: CreateAppointmentRequest = {
          appointment: {
            organizationId,
            branchId: form.branchId || undefined,
            clientId: form.clientId,
            staffUserId: form.staffUserId || undefined,
            staffName: form.staffName || undefined,
            status: Number(form.status) as AppointmentStatus,
            scheduledAt: new Date(form.scheduledAt).toISOString(),
            notes: form.notes || undefined,
          },
          services: serviceInputs,
        };
        const res = await createAppointmentAsync(token, payload);
        if (res.success) {
          closeModal();
          await reload();
          showToast("Appointment created successfully!", "success");
        } else {
          showToast(res.message || "Failed to create appointment", "error");
        }
        return;
      }

      if (modalMode === "edit" && selectedAppointment) {
        const model: AppointmentUpdateModel = {
          branchId: form.branchId || undefined,
          staffUserId: form.staffUserId || undefined,
          staffName: form.staffName || undefined,
          status: Number(form.status) as AppointmentStatus,
          scheduledAt: form.scheduledAt
            ? new Date(form.scheduledAt).toISOString()
            : undefined,
          notes: form.notes || undefined,
          services: serviceInputs,
        };
        const res = await updateAppointmentAsync(
          token,
          selectedAppointment.id,
          model,
        );
        if (res.success) {
          closeModal();
          await reload();
          showToast("Appointment updated successfully!", "success");
        } else {
          showToast(res.message || "Failed to update appointment", "error");
        }
      }
    } catch (err) {
      console.error(err);
      showToast("Something went wrong. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkCompleted = async (appointment: Appointment) => {
    if (appointment.status === AppointmentStatus.Completed) return;
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    setSubmitting(true);
    try {
      const res = await updateAppointmentAsync(token, appointment.id, {
        status: AppointmentStatus.Completed,
      });
      if (res.success) {
        await reload();
        showToast("Appointment marked as completed!", "success");
      } else {
        showToast(res.message || "Failed to update appointment", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Failed to update appointment. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (appointment: Appointment) => {
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    setSubmitting(true);
    try {
      const res = await deleteAppointmentAsync(token, appointment.id);
      if (res.success) {
        await reload();
        showToast("Appointment deleted successfully!", "success");
      } else {
        showToast(res.message || "Failed to delete appointment", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Failed to delete appointment. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClientSubmit = async (form: ClientFormValues) => {
    const { client, message } = await clientPicker.submitNewClient(form);
    if (client) {
      showToast("Client added successfully!", "success");
    } else {
      showToast(message || "Failed to add client", "error");
    }
  };

  const handleFollowUpSubmit = async (form: FollowUpFormValues) => {
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    setSubmitting(true);
    try {
      if (!organizationId) {
        showToast("No active organization", "error");
        return;
      }
      const payload: CreateFollowUpRequest = {
        organizationId,
        branchId: form.branchId || undefined,
        clientId: form.clientId,
        appointmentId: form.appointmentId || undefined,
        serviceId: form.serviceId || undefined,
        title: form.title || undefined,
        notes: form.notes || undefined,
        dueAt: new Date(form.dueAt).toISOString(),
        status: Number(form.status) as FollowUpStatus,
        priority: Number(form.priority),
        assignedTo: form.assignedTo || undefined,
      };
      const res = await createFollowUpAsync(token, payload);
      if (res.success) {
        setFollowUpTarget(null);
        showToast("Follow-up added successfully!", "success");
      } else {
        showToast(res.message || "Failed to add follow-up", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Failed to add follow-up. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const openClientHistory = async (appointment: Appointment) => {
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    setHistoryClient(appointment);
    setHistoryAppointments([]);
    setHistoryError(null);
    setHistoryLoading(true);
    try {
      const res = await getAppointmentsByClientAsync(token, appointment.clientId);
      if (res.success) {
        setHistoryAppointments(res.result ?? []);
      } else {
        setHistoryError(res.message || "Failed to load client history.");
      }
    } catch (err) {
      console.error(err);
      setHistoryError("Failed to load client history. Please try again.");
    } finally {
      setHistoryLoading(false);
    }
  };

  const renderBody = () => {
    if (loading) {
      return <div className="appt-empty">Loading appointments...</div>;
    }

    if (error) {
      return (
        <div className="appt-empty">
          <p>{error}</p>
          <button className="appt-btn appt-btn--secondary" onClick={reload}>
            Retry
          </button>
        </div>
      );
    }

    if (appointments.length === 0) {
      return (
        <div className="appt-empty">
          {filters.search
            ? `No appointments match "${filters.search}" in the selected range.`
            : "No appointments found. Click \"Add Appointment\" to create one."}
        </div>
      );
    }

    return (
      <div className="appt-table-wrap">
        <table className="appt-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Date &amp; Time</th>
              <th>Branch</th>
              <th>Services</th>
              <th>Status</th>
              <th>Staff</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {appointments.map((appointment) => {
              const clientName = appointment.clientName ?? "Unknown client";
              const total = appointment.services.reduce(
                (sum, service) => sum + service.price * service.quantity,
                0,
              );
              return (
                <tr
                  key={appointment.id}
                  className={
                    appointment.status === AppointmentStatus.ScheduledByPublicWeb
                      ? "appt-row--web"
                      : undefined
                  }
                >
                  <td>
                    <div className="appt-name">
                      <span className="appt-avatar">
                        {(appointment.clientName ?? "?").charAt(0).toUpperCase()}
                      </span>
                      <button
                        type="button"
                        className="appt-name__btn"
                        title="View appointment history"
                        aria-label={`View appointment history for ${clientName}`}
                        onClick={() => openClientHistory(appointment)}
                      >
                        {clientName}
                      </button>
                    </div>
                  </td>
                  <td>{formatDateTime(appointment.scheduledAt)}</td>
                  <td>
                    {appointment.branchId
                      ? branchMap.get(appointment.branchId)?.name ?? "—"
                      : "—"}
                  </td>
                  <td>
                    <span className="appt-services">
                      {appointment.services.length}{" "}
                      {appointment.services.length === 1 ? "service" : "services"}
                      {total > 0 && <span className="appt-total"> · {total}</span>}
                    </span>
                  </td>
                  <td>
                    <AppointmentStatusBadge status={appointment.status} />
                  </td>
                  <td>
                    {staffMap.get(appointment.staffUserId ?? "")?.userName ??
                      appointment.staffName ??
                      "—"}
                  </td>
                  <td className="appt-actions">
                    {canEditAppointments && (
                      <button
                        className="appt-icon-btn appt-icon-btn--edit"
                        title="Edit"
                        aria-label={`Edit appointment for ${clientName}`}
                        onClick={() => openModal("edit", appointment)}
                      >
                        <EditIcon />
                      </button>
                    )}
                    {canEditAppointments &&
                      appointment.status !== AppointmentStatus.Completed && (
                        <button
                          className="appt-icon-btn appt-icon-btn--complete"
                          title="Mark as completed"
                          aria-label="Mark as completed"
                          onClick={() => handleMarkCompleted(appointment)}
                        >
                          <CompleteIcon />
                        </button>
                      )}
                    {canCreateFollowUps && (
                      <button
                        className="appt-icon-btn appt-icon-btn--followup"
                        title="Add follow-up"
                        aria-label="Add follow-up"
                        onClick={() => {
                          clientPicker.clearCreatedClient();
                          setFollowUpTarget(appointment);
                        }}
                      >
                        <FollowUpIcon />
                      </button>
                    )}
                    {canDeleteAppointments && (
                      <button
                        className="appt-icon-btn appt-icon-btn--delete"
                        title="Delete"
                        aria-label={`Delete appointment for ${clientName}`}
                        onClick={() => {
                          if (
                            window.confirm(
                              `Delete appointment for "${clientName}"?`,
                            )
                          ) {
                            handleDelete(appointment);
                          }
                        }}
                      >
                        <DeleteIcon />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const startPage = (pagination.page - 1) * pagination.pageSize + 1;
  const endPage = Math.min(
    pagination.page * pagination.pageSize,
    pagination.totalCount,
  );

  if (!entitlementsLoading && entitlements && !entitlements.hasActiveSubscription) {
    return (
      <div className="appt-page">
        <SubscriptionSectionGate section="appointments" />
      </div>
    );
  }

  return (
    <div className="appt-page">
      <div className="appt-header">
        <div>
          <h2>Appointments</h2>
          <p>
            Schedule and manage appointments for{" "}
            {user?.activeOrganization?.name ?? "your organization"}
          </p>
        </div>
        {canCreateAppointments && (
          <button
            className="appt-btn appt-btn--primary"
            onClick={() => openModal("create", null)}
          >
            + Add Appointment
          </button>
        )}
      </div>

      <form
        className="appt-filters"
        onSubmit={(e) => {
          e.preventDefault();
          setFilters({ search: searchInput.trim() || undefined });
        }}
      >
        <label className="appt-filter appt-filter--grow">
          <span>Search</span>
          <input
            type="search"
            placeholder="Client name or phone…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </label>
        <button type="submit" className="appt-btn appt-btn--secondary">
          Search
        </button>
        <DateRangeFilter
          label="Date"
          value={range}
          onChange={handleRangeChange}
        />
        <label className="appt-filter">
          <span>Status</span>
          <select
            value={filters.status === undefined ? "" : String(filters.status)}
            onChange={(e) =>
              setFilters({
                status: e.target.value
                  ? (Number(e.target.value) as AppointmentStatus)
                  : undefined,
              })
            }
          >
            <option value="">All</option>
            {Object.entries(appointmentStatusLabel).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="appt-btn appt-btn--secondary"
          onClick={() => {
            setSearchInput("");
            setRange(emptyDateRangeValue());
            setFilters({
              ...monthRange(),
              status: undefined,
              search: undefined,
            });
          }}
        >
          Reset
        </button>
      </form>

      {renderBody()}

      {!loading && !error && pagination.totalCount > 0 && (
        <div className="appt-pagination">
          <span className="appt-pagination__info">
            Showing {startPage}–{endPage} of {pagination.totalCount}
          </span>
          <div className="appt-pagination__controls">
            <button
              className="appt-btn appt-btn--secondary"
              disabled={!pagination.hasPrevious}
              onClick={() => setPage(pagination.page - 1)}
            >
              Previous
            </button>
            <span className="appt-pagination__page">
              Page {pagination.page} of{" "}
              {Math.max(1, pagination.totalPages)}
            </span>
            <button
              className="appt-btn appt-btn--secondary"
              disabled={!pagination.hasNext}
              onClick={() => setPage(pagination.page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <AppointmentModal
        isOpen={modalOpen}
        mode={modalMode}
        appointment={selectedAppointment}
        branches={branches}
        services={services}
        staff={staff}
        submitting={submitting}
        lookupClient={clientPicker.lookupClient}
        appointmentClient={
          selectedAppointment
            ? {
                fullName: selectedAppointment.clientName,
                phone: selectedAppointment.clientPhone,
                email: selectedAppointment.clientEmail,
                address: selectedAppointment.clientAddress,
                age: selectedAppointment.clientAge,
              }
            : null
        }
        preselectedClient={clientPicker.createdClient}
        onNewClient={clientPicker.openNewClient}
        canCreateClient={canCreateClients}
        onClose={closeModal}
        onSubmit={handleSubmit}
      />

      <ClientModal
        isOpen={clientPicker.clientModalOpen}
        mode="create"
        client={null}
        submitting={clientPicker.submittingClient}
        initial={clientPicker.clientPrefill ?? undefined}
        onClose={clientPicker.closeNewClient}
        onSubmit={handleClientSubmit}
      />

      <FollowUpModal
        isOpen={Boolean(followUpTarget)}
        mode="create"
        followUp={null}
        branches={branches}
        services={services}
        staff={staff}
        submitting={submitting}
        lookupClient={clientPicker.lookupClient}
        appointmentClient={
          followUpTarget
            ? {
                fullName: followUpTarget.clientName,
                phone: followUpTarget.clientPhone,
                email: followUpTarget.clientEmail,
                address: followUpTarget.clientAddress,
                age: followUpTarget.clientAge,
              }
            : null
        }
        preselectedClient={clientPicker.createdClient}
        initial={{
          clientId: followUpTarget?.clientId,
          appointmentId: followUpTarget?.id,
          serviceId: followUpTarget?.services[0]?.serviceId,
        }}
        onNewClient={clientPicker.openNewClient}
        canCreateClient={canCreateClients}
        onClose={() => {
          setFollowUpTarget(null);
          clientPicker.clearCreatedClient();
        }}
        onSubmit={handleFollowUpSubmit}
      />

      <ClientHistoryModal
        isOpen={Boolean(historyClient)}
        clientName={historyClient?.clientName ?? ""}
        clientPhone={historyClient?.clientPhone}
        appointments={historyAppointments}
        loading={historyLoading}
        error={historyError}
        branchMap={branchMap}
        onClose={() => setHistoryClient(null)}
        onRetry={() => {
          if (historyClient) openClientHistory(historyClient);
        }}
      />
    </div>
  );
};

export default AppointmentsPage;